// Two-step OTP login gate for gated (team-visibility) shares.
// Renders email input → "Get code" → code input → "Sign in".
// Calls `onAuthed()` after successful verify.
// Strings are localized via ./i18n (English default, RU parity).

import { requestOtp as defaultRequest, verifyOtp as defaultVerify } from '../auth.js';
import { t } from './i18n.js';

interface AuthApi {
  requestOtp(email: string, apiBase: string): Promise<{ ok: boolean; error?: string }>;
  verifyOtp(email: string, code: string, apiBase: string): Promise<{ ok: boolean; error?: string }>;
}

interface LoginGateOpts {
  teamName: string | null;
  apiBase: string;
  onAuthed: () => void;
  /** Injected auth API — defaults to the real requestOtp/verifyOtp. Tests inject mocks. */
  auth?: AuthApi;
  /** 'portal' for team-portal pages (/login, /library, /dashboard, /setlist); defaults to 'share'. */
  context?: 'share' | 'portal';
}

export function mountLoginGate(host: HTMLElement, opts: LoginGateOpts): void {
  const auth = opts.auth ?? { requestOtp: defaultRequest, verifyOtp: defaultVerify };
  let email = '';

  host.innerHTML = '';

  const wrap = document.createElement('div');
  wrap.className = 'login-gate';

  const title = document.createElement('h2');
  title.textContent = opts.context === 'portal'
    ? t('loginGate.portalTitle')
    : t('loginGate.title');

  const sub = document.createElement('p');
  sub.textContent = opts.context === 'portal'
    ? t('loginGate.portalSub')
    : opts.teamName
      ? t('loginGate.subWithTeam', { teamName: opts.teamName })
      : t('loginGate.subNoTeam');

  wrap.appendChild(title);
  wrap.appendChild(sub);

  const body = document.createElement('div');
  body.className = 'login-gate-body';
  wrap.appendChild(body);

  host.appendChild(wrap);

  function renderEmailStep(): void {
    // Real <form> so Enter submits; the submit handler is THE handler (button is
    // type=button + requestSubmit), preventDefault avoids a page reload.
    const form = document.createElement('form');
    form.noValidate = true;

    const label = document.createElement('label');
    label.htmlFor = 'lg-email-input';
    label.textContent = 'Email';

    const input = document.createElement('input');
    input.id = 'lg-email-input';
    input.type = 'email';
    input.inputMode = 'email';
    input.autocomplete = 'email';
    input.setAttribute('autofocus', '');
    input.placeholder = t('loginGate.emailPlaceholder');

    const err = document.createElement('div');
    err.className = 'gate-err';
    err.setAttribute('role', 'alert');
    err.id = 'lg-email-err';
    input.setAttribute('aria-describedby', err.id);

    const btn = document.createElement('button');
    // type=button + click→requestSubmit funnels Enter and click through the single
    // form submit handler (happy-dom only auto-submits connected forms).
    btn.type = 'button';
    btn.dataset.action = 'request';
    btn.textContent = t('loginGate.requestBtn');

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      email = input.value.trim();
      err.textContent = '';
      const r = await auth.requestOtp(email, opts.apiBase);
      if (!r.ok) {
        err.textContent = t('loginGate.requestError');
        return;
      }
      renderCodeStep();
    });
    btn.addEventListener('click', () => form.requestSubmit());

    label.appendChild(input);
    form.appendChild(label);
    form.appendChild(btn);
    form.appendChild(err);
    body.replaceChildren(form);
    input.focus();
  }

  function renderCodeStep(): void {
    const form = document.createElement('form');
    form.noValidate = true;

    const note = document.createElement('p');
    note.textContent = t('loginGate.codeSent', { email });

    const label = document.createElement('label');
    label.htmlFor = 'lg-code-input';
    label.textContent = t('loginGate.codePlaceholder');

    const code = document.createElement('input');
    code.id = 'lg-code-input';
    code.dataset.field = 'code';
    code.type = 'text';
    code.inputMode = 'numeric';
    code.pattern = '\\d{6}';
    code.autocomplete = 'one-time-code';
    code.maxLength = 6;
    code.setAttribute('autofocus', '');
    code.placeholder = t('loginGate.codePlaceholder');

    const err = document.createElement('div');
    err.className = 'gate-err';
    err.setAttribute('role', 'alert');
    err.id = 'lg-code-err';
    code.setAttribute('aria-describedby', err.id);

    const btn = document.createElement('button');
    btn.type = 'button';
    btn.dataset.action = 'verify';
    btn.textContent = t('loginGate.verifyBtn');

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      err.textContent = '';
      const r = await auth.verifyOtp(email, code.value.trim(), opts.apiBase);
      if (!r.ok) {
        err.textContent = t('loginGate.invalidCode');
        return;
      }
      opts.onAuthed();
    });
    btn.addEventListener('click', () => form.requestSubmit());

    label.appendChild(code);
    form.appendChild(note);
    form.appendChild(label);
    form.appendChild(btn);
    form.appendChild(err);
    body.replaceChildren(form);
    code.focus();
  }

  renderEmailStep();
}
